import React from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { DefaultButton, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack } from '@fluentui/react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/rootReducer';
import useSwitchOrganization from '../hooks/useSwitchOrganization';
import { getOnboardingStepPath } from '../logic/onboardingStepLinks';

interface IResolverParams {
    milestone?: string;
}

/**
 * Resolves `/go/onboarding/<milestone>?org=<organizationId>` from onboarding emails to the screen where
 * that milestone is done. Offers an organization switch first when the link names a different
 * organization than the one selected, the same way the settings deep-link resolver does.
 */
const OnboardingLinkResolver: React.FC<RouteComponentProps<IResolverParams>> = (props) => {
    const history = useHistory();
    const switchOrganization = useSwitchOrganization();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);

    const requestedOrganization = new URLSearchParams(props.location.search).get('org') ?? undefined;
    const target = getOnboardingStepPath(props.match.params.milestone);

    const [switching, setSwitching] = React.useState(false);
    const [organizationSwitched, setOrganizationSwitched] = React.useState(false);
    const [switchFailed, setSwitchFailed] = React.useState(false);

    const needsOrganizationSwitch =
        !!requestedOrganization && !!selectedOrganization && requestedOrganization !== selectedOrganization;

    React.useEffect(() => {
        if (needsOrganizationSwitch && !organizationSwitched) return;
        history.replace(target);
    }, [needsOrganizationSwitch, organizationSwitched, history, target]);

    if (needsOrganizationSwitch && !organizationSwitched) {
        return (
            <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { padding: '1em', maxWidth: 640 } }}>
                <h1>Switch organization?</h1>
                <MessageBar messageBarType={MessageBarType.warning}>
                    This link is for organization <strong>{requestedOrganization}</strong>, but you are working in{' '}
                    <strong>{selectedOrganization}</strong>.
                </MessageBar>
                {switchFailed && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        Could not switch to <strong>{requestedOrganization}</strong>. Your account may not belong to
                        that organization. Sign in with an account that does, or ask an administrator for access.
                    </MessageBar>
                )}
                <Stack horizontal tokens={{ childrenGap: '1em' }}>
                    <PrimaryButton
                        text={switching ? 'Switching…' : `Switch to ${requestedOrganization}`}
                        disabled={switching}
                        onClick={async () => {
                            if (!requestedOrganization) return;
                            setSwitching(true);
                            setSwitchFailed(false);
                            try {
                                await switchOrganization(requestedOrganization);
                                setOrganizationSwitched(true);
                            } catch {
                                setSwitchFailed(true);
                            } finally {
                                setSwitching(false);
                            }
                        }}
                    />
                    <DefaultButton
                        text={`Stay in ${selectedOrganization}`}
                        disabled={switching}
                        onClick={() => setOrganizationSwitched(true)}
                    />
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack styles={{ root: { padding: '1em' } }}>
            <Spinner label='Opening…' />
        </Stack>
    );
};

export default OnboardingLinkResolver;
